// Changes: the monthly review of what the analysts changed. Pick a since and to date
// (effective dates, inclusive); for each book, every change in that window, newest
// first — the analyst's note, who saved it and when, and what moved against the book's
// previous set by fund and by asset class. Exports the same to Excel for the meeting pack.
import React, { useState } from 'react';
import { API, fmtDate, fmtPct, useApiGet } from './apUtils';
import { Notice } from './apComponents';
import { exportChanges } from './exportExcel';

const sydneyToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
const monthStart = (iso) => `${iso.slice(0, 8)}01`;
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', {
  timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
}) : '–');
const KIND = { new: 'Added', sold: 'Sold', up: 'Increased', down: 'Reduced' };

function ChangeCard({ c }) {
  const moved = c.asset_classes.filter((a) => Math.abs(a.change) > 0.00001);
  return (
    <div className="ap-change">
      <div className="ap-change-head">
        <span className="ap-change-date">{fmtDate(c.effective_date)}</span>
        <span className="ap-change-meta">
          {c.source === 'seed' ? 'Loaded from the Refinitiv upload history' : `Saved by ${c.saved_by || 'unknown'} on ${fmtWhen(c.saved_at)}`}
          {' · '}{c.previous_effective_date ? `against the set from ${fmtDate(c.previous_effective_date)}` : 'initial holdings'}
        </span>
      </div>
      <blockquote className={`ap-change-note${c.note ? '' : ' empty'}`}>{c.note || 'No note was saved with this change.'}</blockquote>
      {c.resaved.length > 0 && (
        <Notice kind="warn">
          Re-saved for this date: the set above replaced {c.resaved.length} earlier save{c.resaved.length > 1 ? 's' : ''}
          {' '}({c.resaved.map((r) => `${r.saved_by || 'unknown'} ${fmtWhen(r.saved_at)}${r.note ? `: "${r.note}"` : ''}`).join('; ')}).
        </Notice>
      )}
      <div className="ap-change-body">
        <table className="ap-deck-table ap-change-funds">
          <thead>
            <tr><th className="ap-deck-name">Fund</th><th className="ap-l">APIR</th><th className="ap-l">Asset class</th>
              <th>Change</th><th>Before</th><th>After</th><th>Δ</th></tr>
          </thead>
          <tbody>
            {c.funds.map((f) => (
              <tr key={f.symbol}>
                <td className="ap-deck-name">{f.name}</td>
                <td className="ap-l">{f.apir_code || ''}</td>
                <td className="ap-l">{f.asset_class_group}</td>
                <td><span className={`ap-flag ap-flag-${f.kind === 'sold' ? 'exit' : f.kind}`}>{KIND[f.kind]}</span></td>
                <td>{f.before == null ? '–' : fmtPct(f.before, 2)}</td>
                <td>{f.after == null ? '–' : fmtPct(f.after, 2)}</td>
                <td className={f.change > 0 ? 'ap-up' : 'ap-down'}>{fmtPct(f.change, 2, true)}</td>
              </tr>
            ))}
            {!c.funds.length && <tr><td colSpan={7} className="ap-l">No fund weights changed (re-dated only).</td></tr>}
          </tbody>
        </table>
        {moved.length > 0 && (
          <table className="ap-deck-table ap-change-classes">
            <thead><tr><th className="ap-deck-name">Asset class</th><th>Before</th><th>After</th><th>Δ</th></tr></thead>
            <tbody>
              {moved.map((a) => (
                <tr key={a.name}>
                  <td className="ap-deck-name">{a.name}</td>
                  <td>{fmtPct(a.before, 2)}</td><td>{fmtPct(a.after, 2)}</td>
                  <td className={a.change > 0 ? 'ap-up' : 'ap-down'}>{fmtPct(a.change, 2, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {c.unchanged > 0 && <p className="ap-footnote">{c.unchanged} other fund{c.unchanged > 1 ? 's' : ''} unchanged.</p>}
    </div>
  );
}

export default function ChangesTab() {
  const today = sydneyToday();
  const [since, setSince] = useState(monthStart(today));
  const [to, setTo] = useState(today);
  const valid = since && to && since <= to;
  const { data, error, loading } = useApiGet(valid ? `${API}/changes` : null, { since, to });
  const total = data ? data.books.reduce((n, b) => n + b.changes.length, 0) : 0;

  return (
    <div className="tab-panel">
      <div className="chart-controls">
        <div className="chart-portfolio-selector">
          <label htmlFor="ap-changes-since">Since</label>
          <input id="ap-changes-since" type="date" value={since} max={to || undefined} onChange={(e) => setSince(e.target.value)} />
          <label htmlFor="ap-changes-to">to</label>
          <input id="ap-changes-to" type="date" value={to} min={since || undefined} onChange={(e) => setTo(e.target.value)} />
        </div>
        {data && total > 0 && (
          <button type="button" className="select-all-btn"
            onClick={() => exportChanges(data, `Analyst_Portfolio_Changes_${since}_to_${to}.xlsx`)}>
            Export Excel
          </button>
        )}
        <span className="ap-asat">By effective date, both dates included.</span>
      </div>
      {!valid && <Notice kind="warn">Pick a since date on or before the to date.</Notice>}
      {error && <Notice kind="error">{error}</Notice>}
      {loading && <div className="chart-loading">Loading changes...</div>}
      {data && (
        <>
          <p className="ap-footnote">
            {total} change{total === 1 ? '' : 's'} from {fmtDate(data.since)} to {fmtDate(data.to)}.
          </p>
          {data.books.map((b) => (
            <div key={b.code} className="ap-change-book">
              <h4 className="ap-section-title">{b.name} <span className="ap-footnote">({b.changes.length} change{b.changes.length === 1 ? '' : 's'})</span></h4>
              {b.changes.length ? b.changes.map((c) => <ChangeCard key={c.set_id} c={c} />)
                : <p className="ap-footnote">No changes in this period.</p>}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
